import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert, IconButton, alpha } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

const ShareConfirmationDialog = ({ open, onClose, onConfirm, isPublic, loading = false }) => {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 3
        }}
      >
        <Typography variant="h5" component="span" fontWeight={600}>
          {isPublic ? 'Make Thread Private?' : 'Share This Chat Thread?'}
        </Typography>
        <IconButton
          size="small"
          onClick={loading ? undefined : onClose}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%'
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {isPublic ? (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '0.9375rem' }}>
              Are you sure you want to make this thread private? The share link will no longer work, and anyone who has the link will no
              longer be able to view this thread.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.3),
                borderWidth: 1,
                borderStyle: 'solid',
                '& .MuiAlert-icon': {
                  color: 'warning.main'
                }
              }}
            >
              <Typography variant="body1" fontWeight={500} sx={{ fontSize: '0.9375rem' }}>
                This will make your chat thread publicly accessible via a shareable URL.
              </Typography>
            </Alert>

            <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                'Anyone with the link will be able to view this thread',
                'The thread will be visible without requiring authentication',
                'You can make it private again at any time',
                'The page will not be indexed by search engines'
              ].map((item, idx) => (
                <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'text.secondary',
                      mt: 1.5,
                      flexShrink: 0
                    }}
                  />
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="body1" fontWeight={500} color="text.primary" sx={{ fontSize: '0.9375rem' }}>
              Do you want to continue?
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onConfirm} variant="contained" color={isPublic ? 'error' : 'primary'} disabled={loading} sx={{ px: 4 }}>
          {loading ? 'Processing...' : isPublic ? 'Make Private' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareConfirmationDialog;
